<template>
  <e-chart
    v-if="hasData"
    :options="chartOptions"
    autoresize
    :style="`height: ${this.height}px;`"
  ></e-chart>
  <div v-else class="mt-1-5">{{ $t("No Data Available") }}</div>
</template>

<script>
import axios from 'axios';
import moment from 'moment';

import colorPalette from '@/xvisor/constants/colorPalette';
import colorPaletteShade from '@/xvisor/constants/colorPaletteShade';
import granularity from '@/xvisor/utilities/granularity';
import themeStyle from '@/xvisor/utilities/themeStyle';
import timeFormat from '@/xvisor/utilities/timeFormat';

export default {
  props: {
    urls: {
      type: Object,
      required: true,
    },
    height: {
      Number,
      default: 100,
    },
    chartId: {
      type: String,
      required: true,
    },
    timeRange: {
      type: Object,
      required: true,
    },
    issueTimeSelected: {
      type: Number,
    },
  },
  data() {
    return {
      series: [],
      eventSeries: [],
      loaded: false,
      EventTypeDnsLookupHigh: 11100,
    };
  },
  computed: {
    probeEventParams() {
      return {
        params: {
          start: this.timeRange.start.toISOString(),
          end: this.timeRange.end.toISOString(),
        },
      };
    },
    httpParams() {
      return {
        params: {
          start: this.timeRange.start.toISOString(),
          end: this.timeRange.end.toISOString(),
          stepMinutes: granularity.granularity72(this.timeRange),
        },
      };
    },
    initialMarkline() {
      // Initializes the line to the end of the graph.
      const seriesData = this.series.map((point) => [
        moment(point.time).valueOf(),
        point.lookupTimeMs,
      ]);
      return seriesData.at(-1)[0];
    },
    timeSet() {
      return new Set(this.series.map((item) => item.time));
    },
    hasData() {
      return this.loaded && this.series && this.series.length > 0;
    },
    toggleColor() {
      return themeStyle.styleToggle(colorPalette.black, colorPalette.white);
    },
    timeSeriesFormat() {
      return timeFormat.momentFormat(this.timeSet, new Date());
    },
    markPointHelper() {
      return this.eventSeries.map((event) => ({
        xAxis: event.time,
        yAxis: event.value ? event.value : 0,
        itemStyle: {
          color: event.eventType === this.EventTypeDnsLookupHigh ? 'red' : 'orange',
        },
        // There will always be 1 event for each marker.
        value: 1,
        baseline: event.baseline ? event.baseline : 0,
        name: event.name,
      }));
    },
    maxHeightOfChart() {
      let maxHeight = 0;
      this.series.forEach((point) => {
        maxHeight = Math.max(maxHeight, point.lookupTimeMs);
      });
      this.markPointHelper.forEach((event) => {
        maxHeight = Math.max(maxHeight, event.yAxis);
      });
      return maxHeight;
    },
    chartOptions() {
      return {
        title: {
          left: 40,
          textStyle: {
            color: this.toggleColor,
            fontSize: 12,
          },
        },
        color: [colorPaletteShade.teal4],
        tooltip: {
          trigger: 'axis',
          formatter: (info) => `
            <div class="apache-echarts-tooltip">
              ${moment(info[0].value[0]).format(this.timeSeriesFormat)}
              <br>
              ${info[0].marker} Lookup Time: ${info[0].value[1].toFixed(2)} ms <br>
              ${info[1].marker} Lookup Baseline Time: ${info[1].value[1].toFixed(2)} ms <br>
              ${info[2].marker} Success: ${info[2].value[1].toFixed(2)}% <br>
              ${info[3].marker} Failure: ${info[3].value[1].toFixed(2)}% <br>
            </div>
          `,
          axisPointer: {
            type: 'cross',
            animation: false,
            label: {
              show: false,
            },
          },
        },
        grid: [
          {
            left: 50,
            right: 50,
            top: 30,
            bottom: 30,
          },
        ],
        xAxis: {
          type: 'time',
          boundaryGap: false,
          splitLine: {
            show: false,
          },
          axisTick: {
            lineStyle: {
              color: this.toggleColor,
            },
          },
          axisLine: {
            lineStyle: {
              color: this.toggleColor,
            },
          },
          axisLabel: {
            formatter: (value) => moment(value).format(this.timeSeriesFormat),
            fontSize: 9,
          },
        },
        yAxis: [
          {
            type: 'value',
            name: 'Time (ms)',
            nameLocation: 'center',
            nameGap: 20,
            max: this.maxHeightOfChart,
            axisTick: {
              lineStyle: {
                color: this.toggleColor,
              },
            },
            axisLine: {
              lineStyle: {
                color: this.toggleColor,
              },
            },
            axisLabel: {
              formatter: (value) => `${value}`,
              fontSize: 9,
            },
          },
          {
            type: 'value',
            name: '%',
            nameLocation: 'center',
            nameGap: 25,
            axisTick: {
              lineStyle: {
                color: this.toggleColor,
              },
            },
            axisLine: {
              lineStyle: {
                color: this.toggleColor,
              },
            },
            axisLabel: {
              formatter: (value) => `${value}`,
              fontSize: 9,
            },
          },
        ],
        series: [
          {
            name: 'Lookup Time (ms)',
            type: 'line',
            showSymbol: false,
            yAxisIndex: 0,
            itemStyle: {
              color: colorPalette.purple,
            },
            lineStyle: {
              width: 3,
            },
            data: this.series.map((point) => [
              moment(point.time).valueOf(),
              point.lookupTimeMs,
            ]),
            markPoint: {
              symbol: 'triangle',
              symbolSize: 15,
              label: {
                offset: [0, 4],
                fontSize: 9,
              },
              tooltip: {
                trigger: 'item',
                formatter: (info) => this.tooltipContent(info),
              },
              data: this.eventSeries ? this.markPointHelper : [],
            },
          },
          {
            name: 'Lookup Baseline Time (ms)',
            type: 'line',
            showSymbol: false,
            yAxisIndex: 0,
            itemStyle: {
              color: colorPalette.orange,
            },
            lineStyle: {
              width: 3,
            },
            data: this.series.map((point) => [
              moment(point.time).valueOf(),
              point.lookupBaselineTimeMs,
            ]),
            markLine: {
              symbol: 'circle',
              lineStyle: {
                type: 'solid',
              },
              data: [{ xAxis: this.issueTimeSelected ? this.issueTimeSelected : this.initialMarkline }],
              label: {
                formatter: (info) => moment(info.value).format(this.timeSeriesFormat),
              },
            },
          },
          {
            name: 'Success',
            type: 'bar',
            barWidth: 2,
            yAxisIndex: 1,
            itemStyle: {
              color: colorPalette.green,
            },
            data: this.series.map((point) => {
              if (point.success === 0 && point.failure === 0) {
                return [moment(point.time).valueOf(), 0];
              }
              return [
                moment(point.time).valueOf(),
                (point.success / (point.success + point.failure)) * 100,
              ];
            }),
          },
          {
            name: 'Failure',
            type: 'bar',
            barWidth: 2,
            yAxisIndex: 1,
            itemStyle: {
              color: colorPalette.red,
            },
            data: this.series.map((point) => {
              if (point.success === 0 && point.failure === 0) {
                return [moment(point.time).valueOf(), 0];
              }
              return [
                moment(point.time).valueOf(),
                (point.failure / (point.success + point.failure)) * 100,
              ];
            }),
          },
        ],
      };
    },
  },
  watch: {
    url() {
      this.httpGet();
    },
    timeRange() {
      this.httpGet();
    },
  },
  mounted() {
    this.httpGet();
  },
  methods: {
    calculateUnit(eventName) {
      if (eventName === 'App Available Bandwidth') return 'Mbps';
      if (eventName === 'App Packet Loss') return '';
      return 'ms';
    },
    tooltipContent(info) {
      const cell = info.data;
      const header = `
        <div class="apache-echarts-tooltip">
          <div>
            ${info.marker} ${info.name} / ${moment(cell.xAxis).format(this.timeSeriesFormat)}
          </div>
      `;
      const eventsRow = `<div># Events: ${cell.value > 0 ? cell.value.toFixed(0) : 0}</div>`;
      const unit = this.calculateUnit(cell.name);
      let eventMsgs = '';
      const { xAxis, yAxis, baseline } = cell;
      if (yAxis === 0 || baseline === 0) eventMsgs = '';
      const percentNum = (((yAxis / baseline) - 1) * 100).toFixed(2);
      if (yAxis > baseline) {
        eventMsgs = `
          <div>
            ${moment(xAxis).format(this.timeSeriesFormat)}:
            ${percentNum}% over baseline (${baseline.toFixed(2)}${unit})
          </div>
        `;
      }
      if (info.name === 'App Packet Loss' && yAxis === baseline) {
        eventMsgs = `
          <div>
            ${xAxis}: ${(yAxis * 100).toFixed(2)}% loss (baseline: ${baseline.toFixed(2)}${unit})
          </div>
        `;
      }
      return `${[header, eventsRow, ...eventMsgs].join('')} </div>`;
    },
    httpGet() {
      this.loaded = false;
      axios
        .all([
          this.$http.get(this.urls.tsUrl, this.httpParams),
          this.$http.get(this.urls.eventUrl, this.probeEventParams),
        ])
        .then(axios.spread((tsRes, eventRes) => {
          this.series = tsRes.data;
          this.eventSeries = eventRes.data;
        }))
        .finally(() => { this.loaded = true; });
    },
  },
};
</script>
